import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Alert,
  Fade,
  IconButton,
  Tooltip,
  Avatar,
  alpha,
} from "@mui/material";
import {
  IconReceipt2,
  IconBox,
  IconBolt,
  IconCheck,
  IconArrowsExchange,
  IconArrowBack,
  IconDownload,
  IconPrinter,
  IconShare,
  IconX,
  IconClock,
  IconCreditCard,
  IconBuildingBank,
  IconCalendar,
  IconHash,
  IconCurrencyRupee,
  IconFileInvoice,
  IconUser,
  IconMail,
  IconArrowLeft,
  IconFileText,
  IconPlayerPlay,
  IconAlertCircle,
} from "@tabler/icons-react";
import { format } from "date-fns";
import { getTransactionDetailsByCustomerAndTransactionIdUrl } from "api/hiredrateURLConstruct";
import AppConfig from "api/AppConfig";

// ==============================
// FORMATTERS
// ==============================

const formatCurrency = (amount, currency = "INR") => {
  if (!amount && amount !== 0) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (date) => {
  if (!date) return "N/A";
  const parsedDate = typeof date === "string" ? new Date(date) : new Date(date);
  return format(parsedDate, "dd MMM yyyy, hh:mm a");
};

const formatDateShort = (date) => {
  if (!date) return "N/A";
  const parsedDate = typeof date === "string" ? new Date(date) : new Date(date);
  return format(parsedDate, "dd MMM yyyy");
};

const shortId = (id = "") =>
  id?.length > 30 ? `${id.slice(0, 8)}...${id.slice(-6)}` : id;

// ==============================
// INVOICE COMPONENT
// ==============================

const Invoice = ({ transactionData, onClose, onBack }) => {
  // Extract data
  const transaction = transactionData?.transaction?.transaction || transactionData?.transaction || {};
  const rawData = transaction?.raw_data || {};
  const resource = rawData?.resource || {};
  const invoice = rawData?.transaction?.invoice || {};
  const payment = rawData?.transaction?.payment || {};
  const paymentMethod = rawData?.transaction?.payment_method || {};

  const isSubscription = rawData?.event_type === "subscription";
  const isAddon = rawData?.event_type === "addon";
  const eventType = rawData?.event_type || transaction?.transaction_type || "unknown";
  const eventSubtype = rawData?.event_subtype || "";

  // Subscription specific data
  const changeData = resource?.change || {};
  const oldPlan = changeData?.old_plan || {};
  const newPlan = changeData?.new_plan || {};
  const financials = resource?.financials || {};

  // Addon specific data
  const items = resource?.items || [];
  const summary = resource?.summary || {};

  // ===== STATUS VARIABLES =====
  const paymentStatus = transaction?.payment_status || invoice?.status || "pending";
  const isPaymentSuccessful = paymentStatus === "succeeded" || paymentStatus === "paid";

  const processingStatus = transaction?.processing_status || "pending";
  const isProcessed = transaction?.processed === true;

  const [processing, setProcessing] = useState(false);
  const [processError, setProcessError] = useState("");
  const [processSuccess, setProcessSuccess] = useState(false);



  // ===== BUSINESS MESSAGE =====
  const getBusinessMessage = () => {
    if (isSubscription) {
      return isProcessed
        ? "Your subscription has been upgraded successfully."
        : "Payment received. Subscription upgrade is being processed.";
    }

    if (isAddon) {
      return isProcessed
        ? "Add-on has been activated successfully."
        : "Payment received. Add-on is being activated.";
    }

    return isProcessed
      ? "Transaction completed successfully."
      : "Transaction is being processed.";
  };

  const getTransactionTitle = () => {
    if (isSubscription) {
      if (eventSubtype === "upgrade") return "Plan Upgrade";
      if (eventSubtype === "downgrade") return "Plan Downgrade";
      if (eventSubtype === "renewal") return "Plan Renewal";
      return "Subscription";
    }
    if (isAddon) return "Add-on Purchase";
    return transaction?.transaction_type?.replace("_", " ").toUpperCase() || "Transaction";
  };

  const getStatusInfo = () => {
    if (isPaymentSuccessful) {
      return {
        label: "Paid",
        color: "#10b981",
        bgColor: "#d1fae5",
        icon: <IconCheck size={18} />,
      };
    }
    return {
      label: "Pending",
      color: "#f59e0b",
      bgColor: "#fef3c7",
      icon: <IconClock size={18} />,
    };
  };

  const statusInfo = getStatusInfo();

  // ===== HANDLE PROCESS ADDON =====
  const handleProcessAddon = async () => {
    if (!transaction?.transaction_id || !transactionData?.customer_id) {
      setProcessError("Missing transaction or customer ID");
      return;
    }

    setProcessing(true);
    setProcessError("");
    setProcessSuccess(false);

    try {
      // Extract data from raw_data
      const rawData = transaction?.raw_data || {};
      const resource = rawData?.resource || {};
      const items = resource?.items || [];

      // Get the first addon item (for single addon purchase)
      const firstItem = items[0] || {};

      const payload = {
        customer_id: rawData?.transaction?.customer_id || "", // ← Use Chargebee customer ID from raw_data
        transaction_id: transaction?.transaction_id,
        addon_id: firstItem?.addon_id || "",
        addon_name: firstItem?.name || "",
        quantity: firstItem?.quantity || 1,
        amount: firstItem?.amount || transaction?.total_amount || 0,
        currency: transaction?.currency || "INR",
        invoice_id: transaction?.invoice_id || ""
      };

      const response = await fetch("http://localhost:8069/v1/process-addon-purchase", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.status === "queued") {
        setProcessSuccess(true);
      } else {
        setProcessError(data.detail || data.message || "Failed to process addon");
      }
    } catch (err) {
      setProcessError(err.message || "Something went wrong");
    } finally {
      setProcessing(false);
    }
  };


  // ===== HANDLE VERIFY STATUS =====
  const verifyStatus = () => {
    console.log("View logs for transaction:", transaction?.transaction_id);
  };


  return (
    <Fade in timeout={400}>
      <Box sx={{ width: "100%" }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            bgcolor: "background.paper",
          }}
        >


          {/* ===== HEADER ===== */}
          <Box
            sx={{
              px: { xs: 3, sm: 5 },
              py: { xs: 3, sm: 4 },
              bgcolor: "background.default",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} alignItems="center">
                {onBack && (
                  <IconButton
                    onClick={onBack}
                    sx={{
                      bgcolor: "action.hover",
                      "&:hover": { bgcolor: "action.selected" },
                    }}
                  >
                    <IconArrowLeft size={20} />
                  </IconButton>
                )}

                <Box>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography variant="h5" fontWeight={800}>
                      Transaction
                    </Typography>
                    <Chip
                      label={`#${transaction?.transaction_id || "N/A"}`}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {getTransactionTitle()} • {formatDateShort(transaction?.transaction_date)}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Stack direction="row" spacing={1}>
                  <Chip
                    icon={statusInfo.icon}
                    label={statusInfo.label}
                    sx={{
                      fontWeight: 700,
                      bgcolor: statusInfo.bgColor,
                      color: statusInfo.color,
                      border: "1px solid",
                      borderColor: alpha(statusInfo.color, 0.2),
                      "& .MuiChip-icon": { color: statusInfo.color },
                    }}
                  />

                  <Chip
                    variant="outlined"
                    icon={
                      isProcessed
                        ? <IconCheck size={16} />
                        : <IconClock size={16} />
                    }
                    label={
                      isProcessed
                        ? "Activated"
                        : "Processing"
                    }
                  />
                </Stack>

                {/* ===== PROCESS BUTTONS ===== */}
                <Stack direction="row" spacing={1}>
                  {/* Process Addon Button */}
                  {isAddon && !isProcessed && isPaymentSuccessful && (
                    <Tooltip title="Process this addon purchase manually">
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={
                          processing ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <IconPlayerPlay size={18} />
                          )
                        }
                        onClick={handleProcessAddon}
                        disabled={processing || processSuccess}
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          minWidth: 120,
                        }}
                      >
                        {processing ? "Processing..." : processSuccess ? "Done ✓" : "Process Addon"}
                      </Button>
                    </Tooltip>
                  )}


                </Stack>

                <Tooltip title="Download">
                  <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                    <IconDownload size={18} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Print">
                  <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                    <IconPrinter size={18} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>

          {/* ===== BODY ===== */}
          <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
            {/* ===== PROCESS STATUS ALERTS ===== */}
            {processSuccess && (
              <Alert
                severity="success"
                sx={{ mb: 3, borderRadius: 2 }}
                icon={<IconCheck size={20} />}
              >
                Addon purchase processed successfully! The limits have been updated.
              </Alert>
            )}

            {processError && (
              <Alert
                severity="error"
                sx={{ mb: 3, borderRadius: 2 }}
                icon={<IconAlertCircle size={20} />}
                action={
                  <Button color="inherit" size="small" onClick={handleProcessAddon}>
                    Retry
                  </Button>
                }
              >
                {processError}
              </Alert>
            )}

            {/* ===== AMOUNT HERO ===== */}
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                bgcolor: "primary.light",
                color: "primary.contrastText",
                mb: 4,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: -30,
                  right: -30,
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  bgcolor: "rgba(255,255,255,0.05)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -50,
                  left: -20,
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "rgba(255,255,255,0.03)",
                }}
              />

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={3}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.5 }}>
                    Total Amount
                  </Typography>
                  <Typography variant="h2" fontWeight={800}>
                    {formatCurrency(transaction?.total_amount, transaction?.currency)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* ===== ALERT: BUSINESS MESSAGE ===== */}
            <Alert
              severity={isProcessed ? "success" : "info"}
              sx={{
                mb: 4,
                borderRadius: 2,
              }}
            >
              {getBusinessMessage()}
            </Alert>

            <Grid container spacing={3}>
              {/* ===== LEFT COLUMN: Details ===== */}
              <Grid item xs={12} md={7}>
                <Stack spacing={3}>
                  {/* Payment Method */}
                  {paymentMethod?.type && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        bgcolor: "background.default",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={600} gutterBottom>
                        PAYMENT METHOD
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          sx={{
                            bgcolor: alpha("#6366f1", 0.1),
                            color: "#6366f1",
                            width: 48,
                            height: 48,
                          }}
                        >
                          <IconCreditCard size={24} />
                        </Avatar>
                        <Box>
                          <Typography fontWeight={600}>
                            {paymentMethod?.type?.toUpperCase()} •••• {paymentMethod?.last4}
                          </Typography>
                          <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {paymentMethod?.gateway?.toUpperCase()}
                            </Typography>
                            <Chip
                              size="small"
                              label={paymentMethod?.status?.toUpperCase()}
                              sx={{ height: 20, fontSize: "10px" }}
                            />
                          </Stack>
                        </Box>
                      </Stack>
                    </Paper>
                  )}

                  {/* Subscription Change */}
                  {isSubscription && changeData && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        bgcolor: "background.default",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={600} gutterBottom>
                        PLAN CHANGE
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        {oldPlan?.name && (
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Previous Plan
                            </Typography>
                            <Typography fontWeight={600}>{oldPlan.name}</Typography>
                            {oldPlan.credit_amount && (
                              <Typography variant="caption" color="text.secondary">
                                Credits: {formatCurrency(oldPlan.credit_amount, transaction?.currency)}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {oldPlan?.name && newPlan?.name && (
                          <Box sx={{ display: "flex", alignItems: "center", px: 2 }}>
                            <IconArrowsExchange size={20} color="#94a3b8" />
                          </Box>
                        )}

                        {newPlan?.name && (
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              New Plan
                            </Typography>
                            <Typography fontWeight={600}>{newPlan.name}</Typography>
                            {newPlan.amount && (
                              <Typography variant="caption" color="text.secondary">
                                {formatCurrency(newPlan.amount, transaction?.currency)}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Stack>

                      {financials && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: "background.paper",
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <Stack direction="row" spacing={3} justifyContent="space-around">
                            <Box textAlign="center">
                              <Typography variant="caption" color="text.secondary">
                                New Plan
                              </Typography>
                              <Typography fontWeight={700}>
                                {formatCurrency(financials.new_plan_amount, transaction?.currency)}
                              </Typography>
                            </Box>
                            <Box textAlign="center">
                              <Typography variant="caption" color="text.secondary">
                                Credits Applied
                              </Typography>
                              <Typography fontWeight={700} color="success.main">
                                -{formatCurrency(financials.credit_amount, transaction?.currency)}
                              </Typography>
                            </Box>
                            <Divider orientation="vertical" flexItem />
                            <Box textAlign="center">
                              <Typography variant="caption" color="text.secondary">
                                Net Charge
                              </Typography>
                              <Typography variant="h6" fontWeight={800} color="primary.main">
                                {formatCurrency(financials.net_charge, transaction?.currency)}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      )}
                    </Paper>
                  )}

                  {/* Addon Items */}
                  {isAddon && items.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        bgcolor: "background.default",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={600} gutterBottom>
                        PURCHASED ITEMS
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Item</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: "text.secondary" }} align="center">Qty</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: "text.secondary" }} align="right">Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {items.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <Typography fontWeight={600}>{item?.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {item?.pricing_model}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">{item?.quantity || 1}</TableCell>
                                <TableCell align="right" fontWeight={700}>
                                  {formatCurrency(item?.amount, transaction?.currency)}
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow>
                              <TableCell colSpan={2} align="right" sx={{ fontWeight: 700 }}>
                                Total
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 800, color: "primary.main" }}>
                                {formatCurrency(summary.total_amount || transaction?.total_amount, transaction?.currency)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  )}
                </Stack>
              </Grid>

              {/* ===== RIGHT COLUMN: Quick Info ===== */}
              <Grid item xs={12} md={5}>
                <Stack spacing={2}>
                  {/* ===== TRANSACTION STATUS CARD ===== */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      bgcolor: "background.default",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary" fontWeight={600} gutterBottom>
                      TRANSACTION STATUS
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Stack spacing={2}>
                      <InfoItem
                        icon={<IconCreditCard size={18} />}
                        label="Payment"
                        value={
                          isPaymentSuccessful
                            ? "Successful"
                            : paymentStatus
                        }
                      />

                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{ color: "text.secondary", display: "flex", alignItems: "center" }}>
                          <IconBolt size={18} />
                        </Box>
                        <Typography variant="body2" sx={{ minWidth: 100 }}>
                          Processing
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            flex: 1,
                          }}
                        >
                          {isProcessed
                            ? "Completed"
                            : processingStatus}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<IconFileText size={16} />}
                          onClick={verifyStatus}
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                          }}
                        >
                          Verify Status
                        </Button>
                      </Stack>
                    </Stack>

                    <Box sx={{ mt: 2 }}>
                      <Chip
                        label={
                          isPaymentSuccessful
                            ? "Payment Successful"
                            : "Payment Pending"
                        }
                        sx={{ mr: 1 }}
                      />

                      <Chip
                        variant="outlined"
                        label={
                          isProcessed
                            ? "Activated"
                            : "Processing"
                        }
                      />
                    </Box>
                  </Paper>

                  {/* ===== QUICK INFO ===== */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      bgcolor: "background.default",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary" fontWeight={600} gutterBottom>
                      QUICK INFO
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Stack spacing={2}>
                      <InfoItem
                        icon={<IconHash size={18} />}
                        label="Transaction ID"
                        value={shortId(transaction?.transaction_id)}
                        mono
                      />
                      <InfoItem
                        icon={<IconFileInvoice size={18} />}
                        label="Invoice"
                        value={transaction?.invoice_id || "N/A"}
                      />
                      <InfoItem
                        icon={<IconCalendar size={18} />}
                        label="Date"
                        value={formatDate(transaction?.transaction_date)}
                      />
                      <InfoItem
                        icon={<IconCurrencyRupee size={18} />}
                        label="Currency"
                        value={transaction?.currency || "INR"}
                      />
                      {transaction?.customer_email && (
                        <InfoItem
                          icon={<IconMail size={18} />}
                          label="Customer"
                          value={transaction.customer_email}
                        />
                      )}
                    </Stack>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>

            {/* ===== FOOTER ===== */}
            <Divider sx={{ my: 3 }} />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2}>
                {onClose && (
                  <Button
                    variant="contained"
                    onClick={onClose}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                  >
                    Close
                  </Button>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Paper>
      </Box>
    </Fade>
  );
};

// ==============================
// REUSABLE COMPONENTS
// ==============================

const InfoItem = ({ icon, label, value, mono = false }) => (
  <Stack direction="row" alignItems="center" spacing={1.5}>
    <Box sx={{ color: "text.secondary", display: "flex", alignItems: "center" }}>
      {icon}
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={600}
      sx={{
        fontFamily: mono ? "monospace" : "inherit",
        wordBreak: "break-all",
        flex: 1,
      }}
    >
      {value || "-"}
    </Typography>
  </Stack>
);

// ==============================
// INVOICE CONTAINER
// ==============================

const InvoiceContainer = ({ transactionId, customerId, onClose, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [transactionData, setTransactionData] = useState(null);
  const configObject = AppConfig("APIConfig");

  useEffect(() => {
    if (transactionId && customerId) {
      fetchTransactionDetails();
    }
  }, [transactionId, customerId]);

  const fetchTransactionDetails = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(getTransactionDetailsByCustomerAndTransactionIdUrl
        (customerId, transactionId),
        {
          method: "GET",
          ...configObject,
          headers: {
            ...configObject?.headers,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (data.status === "success") {
        setTransactionData(data);
      } else {
        setError(data.message || "Failed to fetch transaction details");
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {error}
        <Button size="small" onClick={fetchTransactionDetails} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Invoice
      transactionData={transactionData}
      onClose={onClose}
      onBack={onBack}
    />
  );
};

export default InvoiceContainer;
export { Invoice };